import {
  type MemberGroup,
  roleColorHex,
  selectDisplayName,
  selectMemberColor,
  selectMemberGroups,
  selectPresence,
  t,
} from '@alternativamais/chat-core';
import { useVirtualizer } from '@tanstack/react-virtual';
import { memo, useMemo, useRef, useState } from 'react';

import { Avatar } from '../../componentes/Avatar';
import type { Posicao } from '../../componentes/Flutuante';
import { Icone } from '../../componentes/Icone';
import { abrirCamada } from '../../chat/camadas';
import { useDerivado, useEstado } from '../../chat/contexto';
import { MenuMembro } from './perfil/MenuMembro';

type Linha =
  | { tipo: 'titulo'; chave: string; titulo: string }
  | { tipo: 'membro'; chave: string; userId: string; online: boolean };

/**
 * Lista de membros (à direita), como a do Discord: separada pelo cargo que
 * aparece à parte, depois "Online" e "Offline"; só quem vê o canal.
 * Virtualizada: o servidor tem mais de 200 pessoas.
 */
export function Membros({
  guildId,
  canalId,
}: {
  guildId: string;
  canalId: string;
}) {
  const grupos = useDerivado(
    (estado) => selectMemberGroups(estado, guildId, canalId),
    (estado) => [
      estado.members[guildId],
      estado.presences,
      estado.guilds[guildId],
      estado.channels[canalId],
    ],
  );
  const linhas = useMemo(() => achatar(grupos), [grupos]);
  const rolagem = useRef<HTMLDivElement>(null);
  const virtual = useVirtualizer({
    count: linhas.length,
    getScrollElement: () => rolagem.current,
    estimateSize: (indice) => (linhas[indice].tipo === 'titulo' ? 40 : 44),
    getItemKey: (indice) => linhas[indice].chave,
    overscan: 12,
    paddingStart: 8,
  });
  return (
    <aside
      aria-label="Membros"
      className="flex w-60 shrink-0 flex-col bg-lateral"
    >
      <div ref={rolagem} className="rolagem min-h-0 grow overflow-y-auto px-2">
        <div
          className="relative w-full"
          style={{ height: virtual.getTotalSize() }}
        >
          {virtual.getVirtualItems().map((item) => {
            const linha = linhas[item.index];
            return (
              <div
                key={item.key}
                className="absolute top-0 left-0 w-full"
                style={{
                  transform: `translateY(${item.start}px)`,
                  height: item.size,
                }}
              >
                {linha.tipo === 'titulo' ? (
                  <h3 className="m-0 truncate px-2 pt-4 pb-1 text-xs font-semibold tracking-[.04em] text-texto-3 uppercase">
                    {linha.titulo}
                  </h3>
                ) : (
                  <Membro
                    guildId={guildId}
                    canalId={canalId}
                    userId={linha.userId}
                    online={linha.online}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </aside>
  );
}

function achatar(grupos: MemberGroup[]): Linha[] {
  const linhas: Linha[] = [];
  for (const grupo of grupos) {
    const titulo =
      grupo.role?.name ??
      (grupo.id === 'offline' ? t('status.offline') : 'Online');
    linhas.push({
      tipo: 'titulo',
      chave: `g-${grupo.id}`,
      titulo: `${titulo} — ${grupo.members.length}`,
    });
    for (const membro of grupo.members) {
      linhas.push({
        tipo: 'membro',
        chave: membro.user_id,
        userId: membro.user_id,
        online: grupo.id !== 'offline',
      });
    }
  }
  return linhas;
}

const Membro = memo(function Membro({
  guildId,
  canalId,
  userId,
  online,
}: {
  guildId: string;
  canalId: string;
  userId: string;
  online: boolean;
}) {
  const [menu, setMenu] = useState<Posicao | null>(null);
  const usuario = useEstado((estado) => estado.users[userId]);
  const nome = useEstado((estado) =>
    selectDisplayName(estado, userId, guildId),
  );
  const cor = useEstado((estado) => selectMemberColor(estado, guildId, userId));
  const status = useEstado((estado) => selectPresence(estado, userId));
  const dono = useEstado(
    (estado) => estado.guilds[guildId]?.owner_id === userId,
  );
  if (!usuario) return null;
  return (
    <>
      <button
        type="button"
        onClick={(evento) => {
          // O perfil abre à esquerda da lista (como o Discord).
          const caixa = evento.currentTarget.getBoundingClientRect();
          abrirCamada({
            tipo: 'perfil',
            userId,
            guildId,
            ancora: { x: caixa.left - 16 - 340, y: caixa.top },
          });
        }}
        onContextMenu={(evento) => {
          evento.preventDefault();
          setMenu({ x: evento.clientX, y: evento.clientY });
        }}
        className={`flex h-[42px] w-full items-center gap-2.5 rounded-md px-2 text-left hover:bg-hover ${online ? '' : 'opacity-40 hover:opacity-100'}`}
      >
        <Avatar
          pessoa={usuario}
          nome={nome}
          tamanho={32}
          status={online ? status : undefined}
          fundo="var(--cor-lateral)"
        />
        <span className="flex min-w-0 grow items-center gap-1.5">
          <span
            className="truncate text-[15px] font-medium"
            style={{ color: cor ? roleColorHex(cor) : 'var(--cor-texto-2)' }}
          >
            {nome}
          </span>
          {usuario.bot && (
            <span className="shrink-0 rounded bg-destaque-suave px-[5px] py-px text-[10px] font-bold text-destaque-claro">
              BOT
            </span>
          )}
          {dono && (
            <span title="Dono do servidor" className="shrink-0 text-ausente">
              <Icone nome="coroa" tamanho={14} />
            </span>
          )}
        </span>
      </button>
      {menu && (
        <MenuMembro
          userId={userId}
          guildId={guildId}
          canalId={canalId}
          ancora={menu}
          aoFechar={() => setMenu(null)}
        />
      )}
    </>
  );
});
